import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Grid, Autocomplete, TextField
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { getProducts } from '../../../../services/ProductService';
import { resolveUndefinedInvoice } from '../../../../services/SalesService';
import { unitOptions } from '../../../../utils/Enums';

const UndefinedContentModal = ({ open, onClose, invoice, locationId }) => {
  const [contentData, setContentData] = useState({});
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const result = await getProducts(locationId, 1, 100000, '');
        if (result && typeof result === 'object') {
          setAllProducts(result.products || result.data || []);
        } else {
          setAllProducts(result || []);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };

    if (open) {
      fetchProducts();
    } else {
      setContentData({});
    }
  }, [open]);

  const handleChange = (key, product, content) => {
    if (!product || !content) return;
    setContentData(prev => ({
      ...prev,
      [key]: {
        product,
        quantity: content.quantity,
        unit: content.unit,
      }
    }));
  };

  const isComplete = () => {
    if (!invoice) return false;
    return invoice.items.every((item) =>
      item.undefinedContent.every((content, i) => {
        const key = `${item.id}-${content.itemIndex}-${i}`;
        return !!contentData[key];
      })
    );
  };

  const getUnitLabel = (unitId) => {
    const unit = unitOptions.find(u => u.id === unitId);
    return unit ? unit.label : `Unidad ${unitId}`;
  };

  const handleSubmit = async () => {
    if (!invoice) return;

    const items = Object.entries(contentData).map(([key, selected]) => {
      const [invoiceItemIdStr, itemIndexStr] = key.split('-');
      return {
        invoice_item_id: parseInt(invoiceItemIdStr),
        product_id: selected.product.id,
        quantity: selected.quantity,
        unit: selected.unit,
        item_index: parseInt(itemIndexStr),
      };
    });

    try {
      await resolveUndefinedInvoice({
        invoice_id: invoice.id,
        location_id: invoice.location.id,
        items,
      });
      onClose(true);
    } catch (err) {
      console.error(err);
    }
  };

  if (!invoice || !invoice?.items || !Array.isArray(invoice.items)) return null;

  return (
    <Dialog open={open} disableEscapeKeyDown fullWidth maxWidth="sm">
      <DialogTitle>Completar contenido indefinido</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Seleccioná un producto para cada ítem requerido según la categoría correspondiente:
        </Typography>

        <Grid container spacing={3}>
          {invoice.items.map((item, idx) => {
            if (!item.undefinedContent || item.undefinedContent.length === 0) return null;

            return (
              <Grid item xs={12} key={`${item.id}-${idx}`}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {item.productName ?? 'Producto'}
                </Typography>

                <Grid container spacing={2}>
                  {item.undefinedContent.map((content, i) => {
                    const key = `${item.id}-${content.itemIndex}-${i}`;

                    if (!content.categoryId || !content.subCategoryId) return null;

                    const filteredProducts = allProducts.filter(p =>
                      (p.productType === 'DEFINED' || p.productType === 'RETAIL') &&
                      p.category?.id === content.categoryId &&
                      p.subCategory?.id === content.subCategoryId
                    );

                    return (
                      <Grid container item spacing={1} xs={12} key={key}>
                        <Grid item xs={6}>
                          <Autocomplete
                            options={filteredProducts}
                            getOptionLabel={(option) => option?.name ?? ''}
                            value={contentData[key]?.product || null}
                            onChange={(e, newValue) => handleChange(key, newValue, content)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={`Ingrese el producto`}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            label="Cantidad"
                            value={content.quantity}
                            fullWidth
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            label="Unidad"
                            value={getUnitLabel(content.unit)}
                            fullWidth
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          disabled={!isComplete()}
          loading={loading}
        >
          Confirmar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default UndefinedContentModal;
